import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { IoSend, IoClose } from 'react-icons/io5';
import { FiRefreshCw } from 'react-icons/fi';
import { RiSparklingFill } from 'react-icons/ri';
import { images } from '../../constants';
import { askGeminiAssistant } from '../../services/geminiService';
import './ChatWidget.css';

const INITIAL_GREETING = {
  id: 'init-1',
  role: 'model',
  text: "Hi — I'm Sahas's AI assistant. Ask me about his background, his engineering work, or any of his projects.",
};

const SUGGESTIONS = [
  { label: 'What he does', query: 'What does Sahas do?' },
  { label: 'Top projects', query: 'What are his top projects?' },
  { label: 'Tech stack', query: 'What is his tech stack?' },
  { label: 'Experience', query: 'Tell me about his work experience.' },
  { label: 'Contact', query: 'How can I get in touch with Sahas?' },
];

// Helper to format bot markdown text into HTML safely
const formatBotMessage = (text) => {
  if (!text) return '';

  // Parse markdown links [text](url)
  let formatted = text.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer" class="bot-link">$1 ↗</a>'
  );

  // Parse raw URLs that are not already inside an href attribute
  formatted = formatted.replace(
    /(?<!href=")(https?:\/\/[^\s<]+)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer" class="bot-link">$1 ↗</a>'
  );

  // Parse bold text **text**
  formatted = formatted.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

  // Parse inline code `code`
  formatted = formatted.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Convert line breaks and lists
  const lines = formatted.split('\n');
  let inList = false;
  let html = '';

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      if (!inList) {
        html += '<ul class="bot-list">';
        inList = true;
      }
      html += `<li>${trimmed.substring(2)}</li>`;
    } else if (/^\d+\.\s/.test(trimmed)) {
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      const cleanLine = trimmed.replace(/^\d+\.\s/, '');
      html += `<div class="bot-numbered-item">${cleanLine}</div>`;
    } else {
      if (inList) {
        html += '</ul>';
        inList = false;
      }
      if (trimmed) {
        html += `<p class="bot-para">${trimmed}</p>`;
      }
    }
  });

  if (inList) {
    html += '</ul>';
  }

  return html;
};

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([INITIAL_GREETING]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasUnread, setHasUnread] = useState(true);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const isEmptyState = messages.length === 1;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
      setHasUnread(false);
      setTimeout(() => inputRef.current?.focus(), 250);
    }
  }, [isOpen, messages]);

  const handleSend = async (messageText) => {
    const textToSend = (messageText || input).trim();
    if (!textToSend || isLoading) return;

    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      text: textToSend,
    };

    const newHistory = [...messages, userMessage];
    setMessages(newHistory);
    setInput('');
    setIsLoading(true);

    try {
      // Send conversation history (filter out the initial greeting if it's the only one)
      const conversationToSend = newHistory
        .filter((m) => m.id !== 'init-1')
        .map((m) => ({ role: m.role, text: m.text }));

      // If user started from a suggestion, make sure conversation has at least this message
      if (conversationToSend.length === 0) {
        conversationToSend.push({ role: 'user', text: textToSend });
      }

      const reply = await askGeminiAssistant(conversationToSend);

      const botMessage = {
        id: `bot-${Date.now()}`,
        role: 'model',
        text: reply,
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        id: `err-${Date.now()}`,
        role: 'model',
        text: "I can't reach my assistant service right now. Please try again in a moment, or email Sahas directly at belbase.sahas@gmail.com.",
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearChat = () => {
    setMessages([INITIAL_GREETING]);
    setInput('');
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  return (
    <div className="chat-widget-container">
      {/* Floating Toggle Button — hidden while the panel is open */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            className="chat-widget-fab"
            onClick={() => setIsOpen(true)}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.97 }}
            aria-label="Ask Sahas AI"
          >
            <RiSparklingFill className="fab-icon-sparkle" />
            <span className="fab-text">Ask About Me</span>
            {hasUnread && <span className="fab-unread-dot" />}
          </motion.button>
        )}
      </AnimatePresence>

      {/* Floating Chat Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="chat-window"
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.96 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            role="dialog"
            aria-label="Chat with Sahas's AI assistant"
          >
            {/* Header */}
            <header className="chat-header">
              <div className="chat-header-info">
                <span className="chat-avatar-wrapper">
                  <img src={images.profile} alt="" className="chat-avatar" />
                  <span className="chat-status-dot" />
                </span>
                <span className="chat-header-text">
                  <span className="chat-title">Sahas AI</span>
                  <span className="chat-subtitle">Usually replies instantly</span>
                </span>
              </div>

              <div className="chat-header-actions">
                {!isEmptyState && (
                  <button
                    type="button"
                    className="chat-action-btn"
                    onClick={clearChat}
                    title="Start over"
                    aria-label="Start over"
                  >
                    <FiRefreshCw />
                  </button>
                )}
                <button
                  type="button"
                  className="chat-action-btn"
                  onClick={() => setIsOpen(false)}
                  title="Close"
                  aria-label="Close chat"
                >
                  <IoClose />
                </button>
              </div>
            </header>

            {/* Messages */}
            <div className="chat-messages">
              {messages.map((msg, index) => {
                const prev = messages[index - 1];
                const startsGroup = !prev || prev.role !== msg.role;

                return (
                  <div
                    key={msg.id}
                    className={`chat-row ${msg.role} ${startsGroup ? 'starts-group' : ''}`}
                  >
                    {msg.role === 'model' &&
                      (startsGroup ? (
                        <img src={images.profile} alt="" className="chat-row-avatar" />
                      ) : (
                        <span className="chat-row-avatar-spacer" aria-hidden="true" />
                      ))}

                    {msg.role === 'user' ? (
                      <div className="chat-bubble user">{msg.text}</div>
                    ) : (
                      <div
                        className="chat-bubble model"
                        dangerouslySetInnerHTML={{ __html: formatBotMessage(msg.text) }}
                      />
                    )}
                  </div>
                );
              })}

              {/* Starter chips live in the conversation, under the greeting */}
              {isEmptyState && !isLoading && (
                <div className="chat-chips">
                  {SUGGESTIONS.map((suggestion) => (
                    <button
                      key={suggestion.label}
                      type="button"
                      className="chat-chip"
                      onClick={() => handleSend(suggestion.query)}
                    >
                      {suggestion.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Typing Indicator */}
              {isLoading && (
                <div className="chat-row model starts-group">
                  <img src={images.profile} alt="" className="chat-row-avatar" />
                  <div className="chat-bubble model typing-bubble">
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Composer */}
            <div className="chat-input-container">
              <form
                className="chat-input-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSend();
                }}
              >
                <input
                  ref={inputRef}
                  type="text"
                  className="chat-input"
                  placeholder="Ask about Sahas…"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={isLoading}
                />
                <button
                  type="submit"
                  className={`chat-send-btn ${input.trim() ? 'active' : ''}`}
                  disabled={!input.trim() || isLoading}
                  aria-label="Send message"
                >
                  <IoSend />
                </button>
              </form>
              <p className="chat-footer-note">AI answers, based on Sahas's portfolio</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ChatWidget;
